import { useCallback, useMemo } from 'react';

import { type ApolloQueryResult, type OperationVariables, useMutation, useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';

import { tournamentIdAtom } from 'atoms/tournament';
import {
  type InitialDrawDisplaySettings,
  type TournamentDeskUISettings,
} from 'components/dialogs/draw/publicDrawDisplaySettings/types';
import { GET_DRAW_SETTINGS, UPDATE_DRAW_SETTINGS } from 'config/graphql/queries';
import { isStandalone } from 'utils/environment';

import { useUserManagementContext } from './userManagement/useUserManagementContext';

export interface UISettingsService {
  tournamentDeskUI: TournamentDeskUISettings | null;
  updateSettings: (data: Record<string, any>) => Promise<void>;
  getHiddenColumns: (
    defaultHiddenColumnIds: Array<string>,
    allColumns: string[],
    savedColumns?: Array<string>,
  ) => string[];
  refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<InitialDrawDisplaySettings>>;
}

const useUISettings = (): UISettingsService => {
  const { user } = useUserManagementContext() ?? {};
  const tournamentId = useAtomValue(tournamentIdAtom);
  const [update] = useMutation(UPDATE_DRAW_SETTINGS);

  const { data, loading, refetch } = useQuery<InitialDrawDisplaySettings>(GET_DRAW_SETTINGS, {
    fetchPolicy: isStandalone() ? 'cache-only' : 'cache-first',
    variables: { personId: user?.ID.toUpperCase(), tournamentId },
  });

  const tournamentDeskUI = useMemo(
    () => (!isStandalone() ? data?.settings?.tournamentDeskUI : {}),
    [data?.settings?.tournamentDeskUI],
  );

  const getHiddenColumns = useCallback(
    (defaultHiddenColumnIds: Array<string>, allColumns: string[], savedColumns?: Array<string>) => {
      if (!savedColumns && !loading) return defaultHiddenColumnIds;
      else if (!savedColumns && loading) return [];
      return allColumns.filter((column) => !savedColumns?.includes(column));
    },
    [loading],
  );

  const updateSettings = useCallback(
    async (data: Record<string, any>) => {
      if (!isStandalone()) {
        const { data: updatedData } = await refetch();

        const updates = {
          variables: {
            tournamentId,
            personId: user?.ID?.toUpperCase(),
            update: {
              tournamentDeskUI: {
                ...updatedData?.settings?.tournamentDeskUI,
                ...data,
              },
            },
          },
        };

        await update(updates);
        await refetch();
      }
    },
    [tournamentId, user?.ID, update, refetch],
  );

  return { tournamentDeskUI, updateSettings, getHiddenColumns, refetch };
};

export default useUISettings;
